import type { HttpInterceptorFn } from '@angular/common/http';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { AUTH_RETRY, SKIP_AUTH_TOKEN } from '../tokens/http-context.tokens';

const AUTH_PATH_PREFIX = '/api/auth/';

function isAuthEndpoint(url: string): boolean {
  return url.includes(`${AUTH_PATH_PREFIX}login`) || url.includes(`${AUTH_PATH_PREFIX}refresh`);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  let outgoing = req;

  if (!req.context.get(SKIP_AUTH_TOKEN) && !isAuthEndpoint(req.url)) {
    const token = auth.accessToken();

    if (token !== null) {
      outgoing = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }
  }

  return next(outgoing).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      if (
        req.context.get(SKIP_AUTH_TOKEN) ||
        req.context.get(AUTH_RETRY) ||
        isAuthEndpoint(req.url)
      ) {
        return throwError(() => error);
      }

      return auth.refresh().pipe(
        switchMap(() => {
          const token = auth.accessToken();

          if (token === null) {
            return throwError(() => error);
          }

          const retryContext = new HttpContext()
            .set(AUTH_RETRY, true)
            .set(SKIP_AUTH_TOKEN, false);

          return next(
            req.clone({
              setHeaders: { Authorization: `Bearer ${token}` },
              context: retryContext,
            }),
          );
        }),
        catchError((refreshError) => {
          auth.clearSession();
          void router.navigate(['/login']);
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
